import type {
  FoodSearchResult as ApplicationFoodSearchResult,
  FoodSource,
} from "../../../application/ports/food-source";
import type { ExternalFoodCandidate } from "../../../domain/foods/import-draft";
import type { Macronutrients, Micronutrients } from "../../../domain/nutrition/nutrients";
import { OpenFoodFactsClient } from "./client";
import type { ImportedFoodDraft } from "./types";

const ATTRIBUTION = "Food data from Open Food Facts (openfoodfacts.org)";

export class OpenFoodFactsFoodSource implements FoodSource {
  readonly id = "open-food-facts";

  constructor(private readonly client = new OpenFoodFactsClient()) {}

  async search(query: string, limit: number): Promise<readonly ApplicationFoodSearchResult[]> {
    const result = await this.client.search(query, { pageSize: limit });
    return result.items.map(toSearchResult).filter(isPresent);
  }

  async findByBarcode(barcode: string): Promise<ApplicationFoodSearchResult | undefined> {
    const draft = await this.client.findByBarcode(barcode);
    return draft ? toSearchResult(draft) : undefined;
  }
}

function toSearchResult(draft: ImportedFoodDraft): ApplicationFoodSearchResult | undefined {
  if (draft.missingMacros.length > 0 && draft.declaredCaloriesPer100g === undefined) return undefined;
  const candidate: ExternalFoodCandidate = {
    provider: draft.provider,
    providerFoodId: draft.externalId,
    name: draft.name,
    brand: draft.brand,
    barcode: draft.barcode,
    basis: draft.basis,
    measures: draft.measures,
    reportedCalories: draft.declaredCaloriesPer100g,
    macros: mapMacros(draft),
    fibreGrams: draft.nutrition.fibreMg === undefined ? undefined : draft.nutrition.fibreMg / 1_000,
    micronutrients: mapMicronutrients(draft),
  };
  return { candidate, attribution: ATTRIBUTION };
}

function mapMacros(draft: ImportedFoodDraft): Partial<Macronutrients> {
  return {
    ...(!draft.missingMacros.includes("protein") ? { proteinGrams: draft.nutrition.proteinMg / 1_000 } : {}),
    ...(!draft.missingMacros.includes("carbohydrate") ? { carbohydrateGrams: draft.nutrition.carbohydrateMg / 1_000 } : {}),
    ...(!draft.missingMacros.includes("fat") ? { fatGrams: draft.nutrition.fatMg / 1_000 } : {}),
  };
}

function mapMicronutrients(draft: ImportedFoodDraft): Micronutrients {
  return Object.fromEntries(Object.entries(draft.nutrition.micronutrients).map(([key, value]) => [key, { ...value }]));
}

function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined;
}
